import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCloudArrowDown,
  faMagnifyingGlass,
  faPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import FloatCard from '../components/Card/FloatCard'
import PrimaryButton from '../components/Button/PrimaryButton'
import SwitchTab from '../components/Navigation/SwitchTab'
import Pagination from '../components/Navigation/Pagination'
import PerPageSelect from '../components/Navigation/PerPageSelect'
import { TABLE_HEADERS } from '../constants/table-header'
import StatusButton from '../components/Button/StatusButton'
import KorwilSelect from '../components/KorwilSelect'
import PendaftarDetailModal from './PendaftarDetailModal'
import { savePendaftar } from '../utils/exporter'
import RegistrasiModal from '../components/Modal/RegistrasiModal'
import ExportProgressModal from '../components/Modal/ExportProgressModal'
import RejectButton from '../components/Button/RejectButton'
import { TableSkeletonRows } from '../components/Skeleton'
import {
  PAGE_SIZE_OPTIONS,
  fetchAllRegistrations,
  useRegistrations,
} from '../hooks/useRegistrations'
import { isCancelledError } from '../utils/error'
import SearchPendaftarModal, {
  SEARCH_FIELDS,
} from '../components/Modal/SearchPendaftarModal'
import { EMPTY_SEARCH, hasSearch, normalizeSearch } from '../utils/search'
import { toast } from 'react-toastify'
import { formatNumber } from '../utils/formatter'

const PendaftarTable = ({ rowsPerPage = 10 }) => {
  const [status, setStatus] = useState('All')
  const [korwil, setKorwil] = useState('')
  // Pencarian hanya berubah waktu form di modal disubmit, bukan tiap ketikan.
  const [search, setSearch] = useState(EMPTY_SEARCH)
  const [searchModal, setSearchModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(rowsPerPage)
  const [detail, setDetail] = useState(null)
  const [modal, setModal] = useState(false)
  // null = tidak sedang export. Selain itu { loaded, total } dari progress.
  const [exportProgress, setExportProgress] = useState(null)
  const exportAbortRef = useRef(null)

  const { rows, total, pageCount, patchRow, isLoading } = useRegistrations({
    status,
    korwil,
    search,
    page: currentPage,
    pageSize,
  })

  // Kalau jumlah halaman menyusut (misal setelah filter), balik ke halaman terakhir yang ada.
  useEffect(() => {
    if (!isLoading && pageCount > 0 && currentPage > pageCount) {
      setCurrentPage(pageCount)
    }
  }, [isLoading, pageCount, currentPage])

  const appliedSearch = normalizeSearch(search)

  // Baris diperbarui di tempat, tanpa memuat ulang halaman: filter korwil,
  // tab, pencarian, dan halaman yang sedang dibuka semuanya tetap.
  const onRowUpdated = (id, patch) => {
    patchRow(id, patch)
    setDetail((current) =>
      current && current.id === id ? { ...current, ...patch } : current,
    )
  }

  const onSearch = (next) => {
    setCurrentPage(1)
    setSearch(next)
    setSearchModal(false)
  }

  const isExporting = exportProgress !== null
  const isBusy = isLoading
  const firstRowNumber = total === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const lastRowNumber = Math.min(currentPage * pageSize, total)

  // Tabel cuma memuat satu halaman, jadi export mengambil ulang semua data
  // yang cocok dengan filter yang sedang aktif -- halaman demi halaman, dengan
  // progress dan tombol batal karena prosesnya bisa lama.
  const onExport = async () => {
    if (isExporting) {
      return
    }
    const controller = new AbortController()
    exportAbortRef.current = controller
    setExportProgress({ loaded: 0, total: 0 })
    try {
      const allRows = await fetchAllRegistrations({
        status,
        korwil,
        search,
        signal: controller.signal,
        onProgress: setExportProgress,
      })
      if (allRows.length === 0) {
        toast.info('Tidak ada data untuk diexport.')
        return
      }
      savePendaftar(allRows)
    } catch (error) {
      if (isCancelledError(error)) {
        toast.info('Export dibatalkan.')
      } else {
        toast.error('Gagal mengexport data. Silakan coba lagi.')
      }
    } finally {
      exportAbortRef.current = null
      setExportProgress(null)
    }
  }

  const onCancelExport = () => {
    exportAbortRef.current?.abort()
  }

  // Kalau user pindah halaman waktu export masih jalan, request-nya ikut
  // dibatalkan supaya tidak menggantung.
  useEffect(
    () => () => {
      exportAbortRef.current?.abort()
    },
    [],
  )

  return (
    <>
      <FloatCard>
        <div className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-b border-line">
            <div>
              <div className="font-bold text-base text-ink">Data Pendaftar</div>
              <div className="text-[13px] text-muted">
                {isBusy ? 'Memuat data...' : `${formatNumber(total)} pendaftar`}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <PrimaryButton onClick={() => onExport()} loading={isExporting}>
                {isExporting ? 'Menyiapkan...' : 'Export CSV'}
                <FontAwesomeIcon icon={faCloudArrowDown} />
              </PrimaryButton>
              <PrimaryButton onClick={() => setModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
                Registrasi Manual
              </PrimaryButton>
            </div>
          </div>
          <div className="block px-4 py-3 lg:flex lg:justify-between lg:items-center lg:gap-3">
            <div className="flex items-center gap-2 lg:mb-0 mb-2">
              <div className="flex-1 lg:w-[240px] lg:flex-none">
                <KorwilSelect
                  value={korwil}
                  onChange={(v) => {
                    setCurrentPage(1)
                    setKorwil(v)
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => setSearchModal(true)}
                className="flex-none flex items-center gap-2 h-10 px-4 rounded-xl border border-line bg-white text-sm font-semibold text-ink hover:bg-subtle transition-colors"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                Cari
              </button>
            </div>
            <div className="overflow-auto">
              <SwitchTab
                tabs={[
                  { label: 'Semua', value: 'All' },
                  { label: 'Belum verifikasi', value: 'Belum verifikasi' },
                  { label: 'Verif oleh Korwil', value: 'Verif oleh Korwil' },
                  { label: 'Disetujui', value: 'Disetujui' },
                  { label: 'Ditolak', value: 'Ditolak' },
                ]}
                activeTab={status}
                onChange={(v) => {
                  setCurrentPage(1)
                  setStatus(v)
                }}
              />
            </div>
          </div>
          {hasSearch(search) && (
            <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
              <span className="text-[12px] text-muted">Pencarian aktif:</span>
              {SEARCH_FIELDS.filter((field) => appliedSearch[field.name]).map(
                (field) => (
                  <span
                    key={field.name}
                    className="inline-flex items-center gap-2 rounded-full border border-line bg-white pl-3 pr-2 py-1 text-[13px] text-ink"
                  >
                    <span className="text-muted">{field.chip}:</span>
                    {appliedSearch[field.name]}
                    <button
                      type="button"
                      aria-label={`Hapus filter ${field.chip}`}
                      className="w-4 h-4 rounded-full text-muted hover:text-ink transition-colors"
                      onClick={() => onSearch({ ...search, [field.name]: '' })}
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
                    </button>
                  </span>
                ),
              )}
              <button
                type="button"
                className="text-[13px] font-medium text-muted hover:text-ink transition-colors"
                onClick={() => onSearch(EMPTY_SEARCH)}
              >
                Hapus semua
              </button>
            </div>
          )}
          <div>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    {TABLE_HEADERS.map((header) => (
                      <th key={header.label}>{header.label}</th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {isBusy && (
                    <TableSkeletonRows
                      rows={Math.min(pageSize, 10)}
                      columns={TABLE_HEADERS.length + 1}
                    />
                  )}
                  {!isBusy && rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={TABLE_HEADERS.length + 1}
                        align="center"
                        className="py-10 text-muted"
                      >
                        {hasSearch(search)
                          ? 'Tidak ada pendaftar yang cocok dengan pencarian.'
                          : 'Tidak ada data pendaftar.'}
                      </td>
                    </tr>
                  )}
                  {!isBusy &&
                    rows.map((row, index) => (
                      <tr
                        key={row.id ?? index}
                        className="hover cursor-pointer"
                        onClick={() => {
                          setDetail(row)
                        }}
                      >
                        {TABLE_HEADERS.map((header, i) => (
                          <td key={i}>
                            {header.format
                              ? header.format(row[header.value])
                              : row[header.value]}
                          </td>
                        ))}
                        <td>
                          <div className="flex gap-1">
                            <RejectButton data={row} onUpdated={onRowUpdated} />
                            <StatusButton data={row} onUpdated={onRowUpdated} />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2 border-t border-line">
            <div className="flex flex-wrap items-center gap-3">
              <PerPageSelect
                value={pageSize}
                onChange={(v) => {
                  setCurrentPage(1)
                  setPageSize(v)
                }}
                options={PAGE_SIZE_OPTIONS}
              />
              <span className="text-sm text-muted">
                {isBusy
                  ? 'Memuat data...'
                  : `Menampilkan ${formatNumber(firstRowNumber)}-${formatNumber(
                      lastRowNumber,
                    )} dari ${formatNumber(total)} data`}
              </span>
            </div>
            <div className="w-fit mx-auto sm:ml-auto sm:mr-0">
              <Pagination
                totalPage={pageCount}
                currentPage={currentPage}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </FloatCard>
      <PendaftarDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        data={detail}
        onUpdated={onRowUpdated}
      />
      <SearchPendaftarModal
        open={searchModal}
        value={search}
        onClose={() => setSearchModal(false)}
        onSubmit={onSearch}
      />
      <RegistrasiModal open={modal} onClose={() => setModal(false)} />
      <ExportProgressModal
        open={isExporting}
        loaded={exportProgress?.loaded ?? 0}
        total={exportProgress?.total ?? 0}
        onCancel={onCancelExport}
      />
    </>
  )
}

export default PendaftarTable
